// Key numbers for one department, shown on its landing page: cash position, budget used,
// what is waiting, and collected vs spent over the last four quarters. Each card loads and
// fails on its own. Everything here is read under RLS through src/data.
import { useQuery } from '@tanstack/react-query'
import { Link } from '@tanstack/react-router'
import { AlertTriangle, CheckCircle2, ChevronRight } from 'lucide-react'
import { ProgressBar } from '@/components/ui/ProgressBar'
import { MoneyText } from '@/components/ui/MoneyText'
import { getBudget, listBudgetActuals, listBudgetLines, listBudgets } from '@/data/budgets'
import { listExpenses } from '@/data/expenses'
import { listOpenExceptions } from '@/data/reconciliation'
import { listPositions, listRemittances } from '@/data/remittances'
import { formatKwacha } from '@/lib/money'
import { currentQuarterId } from '@/lib/periods'
import { recentQuarters } from '@/lib/reportMath'
import { cn } from '@/lib/cn'
import { CardError, CardLoading, InsightCard } from './Greeting'
import { CollectedSpentChart } from './LazyCharts'
import { budgetUse, departmentTodos, quarterLabel, unconfirmedNgwee } from './insights'
import type { TodoTarget } from './insights'

const linkClass = 'text-body font-medium text-brand hover:underline'

const TARGETS: Record<TodoTarget, '/d/$deptId/budgets' | '/d/$deptId/expenses' | '/d/$deptId/remittances'> = {
  budgets: '/d/$deptId/budgets',
  expenses: '/d/$deptId/expenses',
  remittances: '/d/$deptId/remittances',
}

export function DeptInsights({ deptId, isCentral, canWrite }: { deptId: string; isCentral: boolean; canWrite: boolean }) {
  const quarter = currentQuarterId()
  return (
    <div className="grid gap-4 lg:grid-cols-2">
      {!isCentral && <CashCard deptId={deptId} quarter={quarter} />}
      <BudgetCard deptId={deptId} quarter={quarter} />
      <TodoCard deptId={deptId} canWrite={canWrite} />
      {!isCentral && <HistoryCard deptId={deptId} quarter={quarter} />}
    </div>
  )
}

/** The last four quarters, oldest first (the chart reads left to right). */
const lastFour = (quarter: string) => recentQuarters(quarter, 4).reverse()

function usePositions(deptId: string, quarter: string) {
  const quarters = lastFour(quarter)
  return useQuery({
    queryKey: ['position', deptId, 'recent', quarters],
    queryFn: () => listPositions({ departmentId: deptId, quarterIds: quarters }),
  })
}

function CashCard({ deptId, quarter }: { deptId: string; quarter: string }) {
  const positions = usePositions(deptId, quarter)
  const remittances = useQuery({ queryKey: ['remittances', deptId], queryFn: () => listRemittances(deptId) })
  const now = positions.data?.find((p) => p.quarterId === quarter)

  return (
    <InsightCard
      title="Cash position"
      action={<Link to="/d/$deptId/remittances" params={{ deptId }} className={linkClass}>Money sent</Link>}
    >
      {positions.isPending ? (
        <CardLoading />
      ) : positions.isError ? (
        <CardError error={positions.error} retry={() => void positions.refetch()} />
      ) : (
        <>
          <div>
            <p className="text-body text-muted">Cash you should be holding</p>
            <p className="text-title font-semibold text-ink tabular-nums">{now ? formatKwacha(now.cashOnHandNgwee) : '—'}</p>
          </div>
          <dl className="grid grid-cols-2 gap-x-4 gap-y-2 text-body sm:grid-cols-3">
            <Figure label={`Collected in ${quarterLabel(quarter)}`} ngwee={now?.collectedNgwee ?? 0} />
            <Figure label="Sent to central" ngwee={now?.remittedNgwee ?? 0} />
            <div>
              <dt className="text-muted">Not yet confirmed</dt>
              <dd className="font-medium text-ink">
                {remittances.isPending ? '…' : remittances.isError ? '—' : <MoneyText ngwee={unconfirmedNgwee(remittances.data)} />}
              </dd>
            </div>
          </dl>
        </>
      )}
    </InsightCard>
  )
}

function Figure({ label, ngwee }: { label: string; ngwee: number }) {
  return (
    <div>
      <dt className="text-muted">{label}</dt>
      <dd className="font-medium text-ink">
        <MoneyText ngwee={ngwee} />
      </dd>
    </div>
  )
}

function BudgetCard({ deptId, quarter }: { deptId: string; quarter: string }) {
  const budget = useQuery({ queryKey: ['budget', deptId, quarter], queryFn: () => getBudget(deptId, quarter) })
  const approvedId = budget.data?.status === 'approved' ? budget.data.id : undefined
  const actuals = useQuery({ queryKey: ['budget-actuals', approvedId], queryFn: () => listBudgetActuals(approvedId!), enabled: !!approvedId })
  const lines = useQuery({ queryKey: ['budget-lines', approvedId], queryFn: () => listBudgetLines(approvedId!), enabled: !!approvedId })
  const failed = budget.error ?? actuals.error

  let body
  if (budget.isPending || (approvedId && actuals.isPending)) body = <CardLoading />
  else if (failed) body = <CardError error={failed} retry={() => void (budget.isError ? budget.refetch() : actuals.refetch())} />
  else if (!approvedId) {
    body = (
      <p className="text-body text-muted">
        No approved budget for {quarterLabel(quarter)} yet
        {budget.data ? ` (it is ${budget.data.status === 'submitted' ? 'waiting for approval' : budget.data.status === 'rejected' ? 'sent back' : 'a draft'}).` : '.'}
      </p>
    )
  } else {
    const use = budgetUse(actuals.data ?? [])
    const nameOf = (id: string) => lines.data?.find((l) => l.id === id)?.description ?? 'A line'
    body = (
      <>
        <div className="flex items-baseline justify-between gap-3">
          <p className="text-title font-semibold text-ink tabular-nums">{use.usedPercent}%</p>
          <p className="text-right text-body text-muted">
            {formatKwacha(use.spentNgwee + use.committedNgwee)} of {formatKwacha(use.budgetedNgwee)}
          </p>
        </div>
        <ProgressBar value={use.usedPercent} tone={use.tone} label={`Budget used for ${quarterLabel(quarter)}`} />
        <p className="text-body text-muted">
          {formatKwacha(use.spentNgwee)} paid
          {use.committedNgwee > 0 && `, ${formatKwacha(use.committedNgwee)} approved or waiting but not paid yet`}.
        </p>
        {(use.tone !== 'normal' || use.overLineIds.length > 0) && (
          <p className={cn('flex items-start gap-2 text-body', use.tone === 'warning' ? 'text-wait' : 'text-bad')}>
            <AlertTriangle className="mt-0.5 h-4 w-4 shrink-0" aria-hidden />
            <span>
              {use.tone === 'danger' ? 'Over budget.' : use.tone === 'warning' ? 'Nearly all used.' : ''}
              {use.overLineIds.length > 0 && ` Over on: ${use.overLineIds.map(nameOf).join(', ')}.`}
            </span>
          </p>
        )}
      </>
    )
  }

  return (
    <InsightCard
      title={`Budget used, ${quarterLabel(quarter)}`}
      action={<Link to="/d/$deptId/budgets" params={{ deptId }} className={linkClass}>Budgets</Link>}
    >
      {body}
    </InsightCard>
  )
}

function TodoCard({ deptId, canWrite }: { deptId: string; canWrite: boolean }) {
  const budgets = useQuery({ queryKey: ['budgets', deptId], queryFn: () => listBudgets(deptId) })
  const expenses = useQuery({ queryKey: ['expenses', deptId], queryFn: () => listExpenses(deptId) })
  const exceptions = useQuery({ queryKey: ['exceptions', deptId], queryFn: () => listOpenExceptions(deptId) })
  const failed = budgets.error ?? expenses.error ?? exceptions.error
  const retry = () => {
    for (const q of [budgets, expenses, exceptions]) if (q.isError) void q.refetch()
  }

  let body
  if (budgets.isPending || expenses.isPending || exceptions.isPending) body = <CardLoading />
  else if (failed) body = <CardError error={failed} retry={retry} />
  else {
    const todos = departmentTodos(budgets.data!, expenses.data!, exceptions.data!)
    body =
      todos.length === 0 ? (
        <p className="flex items-center gap-2 text-body text-neutral">
          <CheckCircle2 className="h-5 w-5 text-ok" aria-hidden />
          {canWrite ? "Nothing waiting. You're up to date." : 'Nothing waiting.'}
        </p>
      ) : (
        <ul className="-mx-2 divide-y divide-line">
          {todos.map((t) => (
            <li key={t.key}>
              <Link
                to={TARGETS[t.target]}
                params={{ deptId }}
                className="flex min-h-[44px] items-center gap-3 rounded-control px-2 py-2 hover:bg-page"
              >
                <span
                  className={cn(
                    'inline-flex h-6 min-w-6 items-center justify-center rounded-full px-1.5 text-micro font-semibold tabular-nums',
                    t.urgent ? 'bg-wait-tint text-wait' : 'bg-neutral-tint text-neutral',
                  )}
                >
                  {t.count}
                </span>
                <span className="min-w-0 flex-1">
                  <span className="block text-body font-medium text-ink">{t.label}</span>
                  {t.reason && <span className="block truncate text-body text-muted">“{t.reason}”</span>}
                </span>
                <ChevronRight className="h-4 w-4 shrink-0 text-muted" aria-hidden />
              </Link>
            </li>
          ))}
        </ul>
      )
  }

  return <InsightCard title={canWrite ? 'Things to do' : 'Waiting in this department'}>{body}</InsightCard>
}

function HistoryCard({ deptId, quarter }: { deptId: string; quarter: string }) {
  const positions = usePositions(deptId, quarter)
  const data = lastFour(quarter).map((q) => {
    const p = positions.data?.find((x) => x.quarterId === q)
    return { label: quarterLabel(q), collectedNgwee: p?.collectedNgwee ?? 0, spentNgwee: (p?.spentLocalNgwee ?? 0) + (p?.spentCentralNgwee ?? 0) }
  })
  return (
    <InsightCard title="Collected and spent, last four quarters">
      {positions.isPending ? (
        <CardLoading />
      ) : positions.isError ? (
        <CardError error={positions.error} retry={() => void positions.refetch()} />
      ) : (
        <CollectedSpentChart data={data} height={220} />
      )}
    </InsightCard>
  )
}
